import {
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

const HOSTED_FIELD_BASE_URL =
  process.env.REACT_APP_HOSTED_FIELD_URL ||
  "https://secure.1p6.io/hosted-field.html";
const HOSTED_FIELD_ORIGIN = "https://secure.1p6.io";

const HostedField = forwardRef(
  (
    {
      className = "",
      name = "field",
      placeholder = "text",
      inputType = "text",
      type = "text",
      inputClass,
      cssUrl,
      onInput,
      onFocus,
      onBlur,
      onChange,
      onKeyDown,
      onError,
      ...props
    },
    ref,
  ) => {
    const iframeRef = useRef(null);
    const pendingRequests = useRef(new Map());

    const [iframeUrl, setIframeUrl] = useState();

    useImperativeHandle(ref, () => ({
      getValueToken: () => {
        return new Promise((resolve) => {
          const requestId = `${name}-${crypto.randomUUID()}`;
          pendingRequests.current.set(requestId, resolve);

          iframeRef.current?.contentWindow?.postMessage(
            {
              type: "get-value-token",
              requestId,
              fieldName: name,
            },
            HOSTED_FIELD_ORIGIN,
          );
        });
      },
    }));

    useEffect(() => {
      const handleMessage = (event) => {
        if (event.data.type === "validation-error") {
          const data = event.data;
          if (data.fieldName === name) {
            onError?.(data);
          }
          return;
        }

        if (event.data.type === "token-response") {
          const data = event.data;
          const resolver = pendingRequests.current.get(data.requestId);
          if (resolver) {
            resolver(data.token);
            pendingRequests.current.delete(data.requestId);
          }
          return;
        }

        if (event.data.type === "hosted-field-event") {
          const data = event.data;

          // Only process messages from our field
          if (data.fieldName !== name) return;

          switch (data.eventType) {
            case "input":
              onInput?.(data);
              break;
            case "focus":
              onFocus?.(data);
              break;
            case "blur":
              onBlur?.(data);
              break;
            case "change":
              onChange?.(data);
              break;
            case "keydown":
              onKeyDown?.(data);
              break;
            default:
              break;
          }
        }
      };

      window.addEventListener("message", handleMessage);
      return () => window.removeEventListener("message", handleMessage);
    }, [name, onInput, onFocus, onBlur, onChange, onError]);

    useEffect(() => {
      const url = `${HOSTED_FIELD_BASE_URL}?name=${encodeURIComponent(name)}&placeholder=${encodeURIComponent(placeholder)}&type=${encodeURIComponent(type)}&inputType=${encodeURIComponent(inputType)}${cssUrl ? `&cssUrl=${encodeURIComponent(cssUrl)}` : ""}`;
      setIframeUrl(url);
    }, []);

    const handleIframeLoad = () => {
      if (inputClass) {
        iframeRef.current?.contentWindow?.postMessage(
          { type: "apply-class", fieldName: "secureInput", inputClass },
          HOSTED_FIELD_ORIGIN,
        );
      }
    };

    return (
      <iframe
        ref={iframeRef}
        src={iframeUrl}
        className={`hosted-field ${className}`}
        title={`Hosted field: ${name}`}
        onLoad={handleIframeLoad}
        style={{
          width: "100%",
          height: "65px",
          border: "none",
          display: "block",
          touchAction: "manipulation",
        }}
        {...props}
      />
    );
  },
);

HostedField.displayName = "HostedField";

export default HostedField;
